// QR-Code mit dem offiziellen Bertsy-Logo in der Mitte.
// Gleiche Optik wie im Admin-Dashboard, plus Download- und Druck-Buttons.

import { QRCodeCanvas } from 'qrcode.react';
import { useRef } from 'react';

import { BERTSY_LOGO_SVG } from '../lib/bertsy-logo';

interface Props {
  /** Was im QR codiert wird — typisch das praxisQrPayload-JSON. */
  payload: string;
  /** Anzeigegröße in Pixel. */
  size?: number;
  /** Optionaler Untertitel — meist Praxis-Name + Adresse. */
  caption?: React.ReactNode;
  /** Dateiname für den PNG-Download (ohne Endung). */
  downloadName?: string;
}

// Offizielles Bertsy-Logo als inline SVG-Data-URL — kein externer Asset-Lookup,
// und damit auch im PNG-Download (toDataURL) korrekt eingebettet, weil kein
// Cross-Origin-Cleartainting auftritt.
const LOGO_DATA_URL = 'data:image/svg+xml;utf8,' + encodeURIComponent(BERTSY_LOGO_SVG);

export function BertsyQrCode({ payload, size = 260, caption, downloadName = 'bertsy-qr' }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);

  const onDownload = () => {
    const canvas = wrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${downloadName}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onPrint = () => window.print();

  // Logo-Größe = ~20% der QR-Größe. Mit Error-Correction "H" bleibt der
  // QR auch bei dieser Verdeckung scannbar.
  const logoSize = Math.round(size * 0.2);

  return (
    <div style={{ textAlign: 'center' }}>
      <div
        ref={wrapRef}
        style={{
          display: 'inline-block',
          padding: 20,
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #DDE9E8',
        }}
      >
        <QRCodeCanvas
          value={payload}
          size={size}
          bgColor="#FFFFFF"
          fgColor="#1D1D1B"
          level="H"
          marginSize={2}
          imageSettings={{
            src: LOGO_DATA_URL,
            height: logoSize,
            width: logoSize,
            excavate: true,
          }}
        />
      </div>
      {caption && <div style={{ marginTop: 12, fontSize: 13, color: '#5F6B68' }}>{caption}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
        <button type="button" className="btn btn-primary" onClick={onDownload}>📥 PNG herunterladen</button>
        <button type="button" className="btn btn-ghost" onClick={onPrint}>🖨 Drucken</button>
      </div>
    </div>
  );
}
